import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useOrg } from "./org";
import { Initials } from "./people/bits";

const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const source = (i) => (i.mode === "lists" ? `Lists: ${i.lists.map((l) => l.name).join(", ")}` : "All active people");

const UndoConfirm = ({ d, onDone, onCancel }) => {
  const { api } = useOrg();
  const [busy, setBusy] = useState(false);
  const undo = () => {
    setBusy(true);
    api(`/integrations/pco/imports/${d.id}/undo`, { method: "POST" })
      .then((r) => { toast.success(`Import undone: ${r.removed} removed, ${r.kept} kept`); onDone(); })
      .catch((e) => toast.error(e.message)).finally(() => setBusy(false));
  };
  return (
    <div className="mt-4 p-4 rounded-lg border border-sun/40 bg-sunk" data-testid="pco-undo-confirm">
      <p className="t-name-sm">Undo this import?</p>
      <p className="t-body mt-2">{d.will_remove} {d.will_remove === 1 ? "person has" : "people have"} no care history and will be removed from Care.</p>
      {d.will_keep > 0 && <p className="t-body mt-1">{d.will_keep} {d.will_keep === 1 ? "person has" : "people have"} care history. They'll stay, with everything Care knows about them, but move to Not Monitored and show as no longer included by Planning Center.</p>}
      <p className="t-meta mt-2">Nothing is changed in Planning Center.</p>
      <div className="flex gap-2 mt-4">
        <button disabled={busy} onClick={undo} className="btn btn-primary btn-sm" data-testid="pco-undo-confirm-btn">{busy ? "Undoing…" : "Yes, undo this import"}</button>
        <button onClick={onCancel} className="btn btn-quiet btn-sm" data-testid="pco-undo-cancel">Keep it</button>
      </div>
    </div>
  );
};

const Detail = ({ id, onChanged }) => {
  const { api } = useOrg();
  const [d, setD] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const load = () => api(`/integrations/pco/imports/${id}`).then(setD).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <p className="t-meta py-3">Loading…</p>;
  return (
    <div className="pb-4" data-testid={`pco-import-detail-${id}`}>
      <p className="t-meta">{d.people.length} people from this import are still in Care{d.already_gone ? ` · ${d.already_gone} already removed` : ""}</p>
      <div className="max-h-72 overflow-y-auto mt-2 rule-list border-y border-line">
        {d.people.map((p) => (
          <div key={p.id} className="py-2 flex items-center gap-3" data-testid={`pco-import-person-${p.id}`}>
            <Initials p={p} size={28} /><span className="t-body flex-1 truncate">{p.first_name} {p.last_name}</span>
            {p.has_care_history && <span className="t-meta">Has care history</span>}
          </div>
        ))}
      </div>
      {d.status === "active" && d.people.length > 0 && (confirm ? <UndoConfirm d={d} onCancel={() => setConfirm(false)} onDone={() => { setConfirm(false); load(); onChanged(); }} />
        : <button onClick={() => setConfirm(true)} className="btn btn-outline btn-sm mt-4" data-testid={`pco-undo-btn-${id}`}>Undo import</button>)}
    </div>
  );
};

export const ImportHistory = () => {
  const { api } = useOrg();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api("/integrations/pco/imports").then(setList).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="mt-8" data-testid="pco-import-history">
      <h3 className="t-label">Import history</h3>
      {!list ? <p className="t-meta mt-3">Loading…</p> : !list.length ? <p className="t-meta mt-3" data-testid="pco-import-history-empty">No imports have added people yet.</p> : (
        <div className="rule-list border-y border-line mt-3">
          {list.map((i) => (
            <div key={i.id} data-testid={`pco-import-${i.id}`}>
              <button onClick={() => setOpen(open === i.id ? null : i.id)} className="w-full text-left py-4" data-testid={`pco-import-open-${i.id}`}>
                <p className="t-name-sm">{when(i.at)}{i.status === "undone" && <span className="t-meta font-sans ml-2" data-testid={`pco-import-undone-${i.id}`}>Undone</span>}</p>
                <p className="t-meta">{source(i)}</p>
                <p className="t-meta">{i.created} created · {i.matched} linked to existing · {i.review} sent to review · {i.households_created} households created</p>
                {i.undo_result && <p className="t-meta">Undo: {i.undo_result.removed} removed · {i.undo_result.kept} kept with care history</p>}
              </button>
              {open === i.id && <Detail id={i.id} onChanged={load} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
